/**
 * 会话持久化（IndexedDB）—— 对拍 climb-core/src/session.rs 语义
 *
 * - 会话身份 = 原始文件字节 SHA-256（跨刷新/重开稳定；桌面版用「同目录同名」）
 * - 每次编辑操作后自动落盘（auto_save）；恢复时优先会话，其次全新分析
 * - 恢复只存分析分辨率 RGBA，HSV/Lab 加载时重算（session.rs:79 同构）
 * - 原图 Blob 一并存（导出需要全分辨率）；undo 栈不持久化（与桌面一致）
 */
import { computeHsv } from "./color";
import type { Analysis, Cluster, Hold, Params } from "./types";

const DB_NAME = "grip-sessions";
const DB_VERSION = 1;
const STORE = "sessions";

export interface StoredSession {
  /** 原始文件 SHA-256（hex） */
  id: string;
  name: string;
  savedAt: number;
  params: Params;
  selected: number | null;
  excluded: number[];
  erased: number[];
  person: Uint32Array | null;
  holds: Hold[];
  clusters: Cluster[];
  labels: Uint32Array;
  aw: number;
  ah: number;
  fw: number;
  fh: number;
  wallV: number;
  wallS: number;
  timingMs: number;
  /** 分析分辨率 RGBA（恢复时重算 HSV） */
  img: Uint8ClampedArray;
  /** 原始文件（导出全分辨率用） */
  origBlob: Blob;
}

export class SessionStoreError extends Error {}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new SessionStoreError("IndexedDB 不可用"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () =>
      reject(new SessionStoreError(`IndexedDB 打开失败：${req.error?.message}`));
  });
}

export async function saveSession(s: StoredSession): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const req = db.transaction(STORE, "readwrite").objectStore(STORE).put(s);
      req.onsuccess = () => resolve();
      req.onerror = () =>
        reject(new SessionStoreError(`会话写入失败：${req.error?.message}`));
    });
  } finally {
    db.close();
  }
}

export async function loadSession(id: string): Promise<StoredSession | undefined> {
  const db = await openDb();
  try {
    return await new Promise<StoredSession | undefined>((resolve, reject) => {
      const req = db.transaction(STORE, "readonly").objectStore(STORE).get(id);
      req.onsuccess = () => resolve(req.result as StoredSession | undefined);
      req.onerror = () =>
        reject(new SessionStoreError(`会话读取失败：${req.error?.message}`));
    });
  } finally {
    db.close();
  }
}

export async function deleteSession(id: string): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const req = db.transaction(STORE, "readwrite").objectStore(STORE).delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () =>
        reject(new SessionStoreError(`会话删除失败：${req.error?.message}`));
    });
  } finally {
    db.close();
  }
}

/** StoredSession → Analysis（重算 HSV；标签图与人物掩码直接带回） */
export function sessionToAnalysis(s: StoredSession): Analysis {
  const { hue, sat, val } = computeHsv(s.img);
  return {
    aw: s.aw,
    ah: s.ah,
    fw: s.fw,
    fh: s.fh,
    labels: s.labels,
    holds: s.holds,
    clusters: s.clusters,
    img: s.img,
    hue,
    sat,
    val,
    person: s.person,
    wallV: s.wallV,
    wallS: s.wallS,
    timingMs: s.timingMs,
  };
}
